import { FormikProps } from "formik";
import useCountries from "../../../hooks/useCountries";
import CloseIcon from "../../icons/CloseIcon";
import SuperDropdown from "../../dropdown/SuperDropdown";
import DefaultDropdownItem from "../../dropdown/DefaultDropdownItem";
import ValidationErrorMessage from "../ValidationErrorMessage";
import IsRequired from "../common/IsRequired";
import { TextInputFormik } from "../InputItem";
import { IAndenAdresse } from "../../../interfaces/formDto";

type AddressGenericFormSetProps<U extends IAndenAdresse> = {
  formik: FormikProps<U>;
  hasLandekode?: boolean;
  hasCo?: boolean;
  title?: string | null;
};

export function AddressGenericFormSet<U extends IAndenAdresse>({
  formik,
  hasCo = true,
  hasLandekode = true,
  title = "Adresse",
}: AddressGenericFormSetProps<U>) {
  const { countries, getCountryByCountryName, getCountryByCountryCode } =
    useCountries();
  const { setFieldValue, setFieldTouched, values, initialValues } = formik;
  return (
    <>
      {title?.length && <h5 className="flex-100 mb-n2">{title}</h5>}
      <div className="w-100 mb-n2">
        <TextInputFormik
          formik={formik}
          config={{
            component: "textinput",
            id: "andenAdresse.adresselinie1",
            initialValue: initialValues.andenAdresse?.adresselinie1 ?? "",
            label: "Adresselinje 1",
            isRequired: true,
          }}
        />
      </div>
      <div className="w-100 mb-n2">
        <TextInputFormik
          formik={formik}
          config={{
            component: "textinput",
            id: "andenAdresse.adresselinie2",
            initialValue: initialValues.andenAdresse?.adresselinie2 ?? "",
            label: "Adresselinje 2",
            isRequired: false,
          }}
        />
      </div>
      <div className="w-100 mb-n2">
        <TextInputFormik
          formik={formik}
          config={{
            component: "textinput",
            id: "andenAdresse.postnummer",
            initialValue: initialValues.andenAdresse?.postnummer ?? "",
            label: "Postnummer",
            isRequired: true,
          }}
        />
      </div>
      <div className="w-100 mb-n2">
        <TextInputFormik
          formik={formik}
          config={{
            component: "textinput",
            id: "andenAdresse.bynavn",
            initialValue: initialValues.andenAdresse?.bynavn ?? "",
            label: "By",
            isRequired: true,
          }}
        />
      </div>
      <div className={`w-100 ${hasLandekode || hasCo ? " mb-n2" : ""}`}>
        <TextInputFormik
          formik={formik}
          config={{
            component: "textinput",
            id: "andenAdresse.adresselinie4",
            initialValue: initialValues.andenAdresse?.adresselinie4 ?? "",
            label: "Adresselinje 4",
            isRequired: false,
          }}
        />
      </div>
      {hasCo && (
        <div className={`w-100 ${hasLandekode ? " mb-n2" : ""}`}>
          <TextInputFormik
            formik={formik}
            config={{
              component: "textinput",
              id: "andenAdresse.coNavn",
              initialValue: initialValues.andenAdresse?.coNavn ?? "",
              label: "Co",
              isRequired: false,
            }}
          />
        </div>
      )}

      {hasLandekode && (
        <div className="w-100 ">
          <label className="form-label w-100">
            Land <IsRequired />
            {values.andenAdresse?.landekode ? (
              <button
                type="button"
                className="form-control w-100 d-flex align-items-center justify-content-between"
                onClick={() =>
                  setFieldValue("andenAdresse.landekode", "", true)
                }
              >
                <span>
                  {getCountryByCountryCode(values.andenAdresse.landekode)?.Navn}
                </span>
                <CloseIcon />
              </button>
            ) : (
              <SuperDropdown
                onBlur={() => setFieldTouched("andenAdresse.landekode", true)}
                classNameContainer={`${
                  formik.touched["andenAdresse"]?.["landekode"] &&
                  formik.errors["andenAdresse"]?.["landekode"]
                    ? "is-invalid"
                    : ""
                }`}
                items={countries}
                itemsIdKey="Id"
                itemToString={(item) => item?.Navn ?? ""}
                itemToElement={(item) => (
                  <DefaultDropdownItem label={item.Navn} />
                )}
                filterKeys={["Navn", "Landekode"]}
                onSelectedItemEvent={(item) => {
                  if (item.selectedItem) {
                    setFieldTouched("andenAdresse.landekode", true);
                    setFieldValue(
                      "andenAdresse.landekode",
                      getCountryByCountryName(item.selectedItem?.Navn)
                        ?.Landekode ?? "",
                      true,
                    );
                  }
                }}
              />
            )}
          </label>
          <ValidationErrorMessage
            touched={formik.touched["andenAdresse"]?.["landekode"]}
            errorMessage={formik.errors["andenAdresse"]?.["landekode"]}
          />
        </div>
      )}
    </>
  );
}
